import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SelectItem } from '@openng/optimus-ui/api';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { VIEW_AUDIT } from 'src/app/constants/action-constants';
import {
  EXTERNAL_LIST_DETAIL_CONTAINER,
  EXTERNAL_LIST_SEARCH_CONTAINER,
} from 'src/app/constants/common.constants';
import {
  EXT_CONTACT_LIST_TAB_PREFIX,
  NEW_EXT_CONTACT_LIST,
} from 'src/app/constants/contact-constants';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import { getDirtyValues } from 'src/app/core/functions/common-functions';
import { BreadCrumb } from 'src/app/model/common/bread-crumb';
import { Column } from 'src/app/model/common/column';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { ExternalContactList } from 'src/app/model/contacts/external-contact-list';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { ContactListApiService } from 'src/app/services/api/contact-list-api.service';
import { ContactListBuilderService } from 'src/app/services/builder/contact-list-builder.service';
import { ExternalContactListTableService } from 'src/app/services/table/external-contact-list-table.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { getExternalContactListMemberDefinition } from '../../definitions/external-contact-list-member-table-definition';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { ExternalContactListMember } from 'src/app/model/contacts/external-contact-list-member';
import { SelectChangeEvent, Select } from '@openng/optimus-ui/select';
import { AuditDialogContainerComponent } from 'src/app/core/containers/audit-dialog-container.component';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { InputText } from '@openng/optimus-ui/inputtext';
import { MultiSelect } from '@openng/optimus-ui/multiselect';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { InputMask } from '@openng/optimus-ui/inputmask';
import { TranslatePipe } from '@ngx-translate/core';
import { BlockableDiv } from 'src/app/common/components/blockable-div/blockable-div.component';

@Component({
  selector: 'app-external-contact-list-details-container',
  templateUrl: './external-contact-list-details-container.component.html',
  styleUrls: ['./external-contact-list-details-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    BlockUI,
    FormsModule,
    ReactiveFormsModule,
    Select,
    ButtonDirective,
    InputText,
    MultiSelect,
    GeneralGridComponent,
    Dialog,
    Tooltip,
    InputMask,
    TranslatePipe,
    BlockableDiv,
  ],
})
export class ExternalContactListDetailsContainerComponent
  extends DetailsContainer
  implements OnInit, OnDestroy
{
  protected _contactListTableService = inject(ExternalContactListTableService);
  protected _contactListApi = inject(ContactListApiService);
  protected _contactListBuilder = inject(ContactListBuilderService);
  protected _fb = inject(UntypedFormBuilder);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _breadCrumbService = inject(BreadCrumbBuilderService);
  protected _authApi = inject(AuthApiService);
  protected _dialogService = inject(DialogService);

  DirtyStatus = DirtyStatus;
  contactList: ExternalContactList | null = null;
  loaded = false;
  saving = false;
  backToSearchResult!: BreadCrumb;
  listBreadCrumb!: BreadCrumb;

  isEditting = false;
  isEdittingMember = false;
  members: any[] = [];
  memberColumns: Column[] = [];
  displayDialog = false;
  externalContactCreateForm!: UntypedFormGroup;
  canCreate = false;
  canEdit = false;
  canDelete = false;
  actions: SelectItem[] = [];
  selectedAction: any = null;
  breadCrumbSub!: Subscription;
  availableBusinessUnits: SelectItem[] = [];

  constructor() {
    super();
  }

  ngOnInit() {
    this.memberColumns = getExternalContactListMemberDefinition();
    this.setupTabClosingSubscription();
    this.setupBreadCrumbSub();
    this.externalContactCreateForm = this._fb.group({
      id: null,
      name: [null, Validators.required],
      description: null,
      phone: [null, Validators.required],
    });

    this.initialize();
  }

  ngOnDestroy(): void {
    if (this.tabClosingSub) {
      this.tabClosingSub.unsubscribe();
    }

    if (this.valueSub) {
      this.valueSub.unsubscribe();
    }

    if (this.breadCrumbSub) {
      this.breadCrumbSub.unsubscribe();
    }
  }

  setupBreadCrumbSub(): void {
    this.breadCrumbSub =
      this._breadCrumbService.breadCrumbSelectedEvent.subscribe(
        (breadCrumb) => {
          this._breadCrumbService.removeBreadCrumb(this.listBreadCrumb);
          const tab = this._tabService.buildNewTab(
            EXTERNAL_LIST_SEARCH_CONTAINER,
            true
          );
          this._tabService.setMobileTab(tab);
        }
      );
  }

  initialize() {
    this.availableBusinessUnits = this._authApi
      .getUserBusinessUnits()
      .map((bu) => {
        return { label: bu.name, value: bu.id };
      });

    const id = this.id;
    this.contactList = this._contactListTableService.getSelected();
    if (this.contactList && this.contactList.id) {
      this.setupScreen();
    } else if (id) {
      // use network query to find contact list by id.
      this._contactListApi.getExternalContactList(id).subscribe({
        next: (list) => {
          this.contactList = list;
          this.doCheckComplete = false;
          this.setupScreen();
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.RETRIEVE_ERROR',
            bodyKey: 'COMMON.MESSAGES.ERROR.RETRIEVE_ERROR_MESSAGE',
          });
        },
      });
      this.updateActionsList();
    } else {
      this.contactList = this._contactListTableService.getSelected();

      if (!this.contactList) {
        this._logAndMessage.errorLogOnly('Contact List was null');
        if (this._deviceService.isMobile()) {
          const tab = this._tabService.buildNewTab(
            EXTERNAL_LIST_SEARCH_CONTAINER,
            true
          );
          this._tabService.setMobileTab(tab);
        } else {
          const index = this._tabService.getActiveIndex();
          this._tabService.closeTab(index);
        }
      } else {
        if (!this.contactList.id) {
          this.isEditting = true;
        }
        this.setupScreen();
      }
    }
  }

  async setupScreen() {
    if (this._deviceService.isMobile()) {
      this._translateService
        .get('CONTACT.SCREEN.EXTERNAL_CONTACT_LIST')
        .subscribe((label) => {
          this._breadCrumbService.resetAndAddBreadCrumb(
            new BreadCrumb(label, null, true)
          );
          if (this.contactList) {
            this.listBreadCrumb = new BreadCrumb(
              this.contactList.name!,
              null,
              false
            );
            this._breadCrumbService.addBreadCrumb(this.listBreadCrumb);
          }
        });
    } else {
      if (isNullOrUndefined(this.contactList!.id)) {
        const label = await firstValueFrom(
          this._translateService.get('CONTACT.LABEL.NEW_EXTERNAL_LIST')
        );
        this._tabService.updateActiveTabLabel(label);
      } else {
        const label = await firstValueFrom(
          this._translateService.get('CONTACT.LABEL.EXTERNAL_LIST')
        );
        const tab = this._tabService.getTabById(this.contactList!.id);
        this._tabService.updateTabLabel(
          tab!,
          label + ':' + this.contactList!.name
        );
      }
    }

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTACTS.EXTERNAL_LISTS.CREATE_EXTERNAL_LISTS,
    ]);
    this.canDelete = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTACTS.EXTERNAL_LISTS.DELETE_EXTERNAL_LISTS,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTACTS.EXTERNAL_LISTS.EDIT_EXTERNAL_LISTS,
    ]);
    this.buildForm();
    this.updateForm();
    this.loading = false;
  }

  buildForm() {
    this.form = this._fb.group({
      name: [null, Validators.required],
      notes: null,
      businessUnits: [null, Validators.required],
      members: this._fb.array([]),
    });

    this.valueSub = this.form.valueChanges.subscribe((changes) => {
      if (this.form?.dirty) {
        this.changesEvent.emit({
          isDirty: true,
          index: this.index(),
          id: this.id,
        });
      }
    });
  }

  updateForm() {
    if (this.contactList) {
      this.form?.patchValue({
        name: this.contactList.name,
        notes: this.contactList.notes,
        businessUnits: this.contactList.businessUnits.map((bu) => bu.id),
      });
      this.members = [...this.contactList.members];
    }
    if (!this.isEditting) {
      this.form?.disable();
    }
  }

  editMember() {
    this.externalContactCreateForm.enable();
    this.isEdittingMember = true;
  }

  addMember() {
    this.isEdittingMember = true;
    this.externalContactCreateForm.patchValue({
      id: null,
      name: null,
      description: null,
      phone: null,
    });
    this.externalContactCreateForm.enable();
    this.displayDialog = true;
  }

  modifyMember($event: { data: ExternalContactListMember }) {
    if ($event) {
      this.externalContactCreateForm.patchValue({
        id: $event.data.id,
        name: $event.data.name,
        description: $event.data.description,
        phone: $event.data.phone,
      });
      this.externalContactCreateForm.disable();
      this.displayDialog = true;
    }
  }

  updateMember() {
    const raw = this.externalContactCreateForm.getRawValue();
    const array = this.contactList?.members.filter((m) => m.id === raw.id);
    if (array && array.length > 0) {
      const member = array[0];
      member.dirtyStatus = DirtyStatus.UPDATED;
      member.name = raw.name;
      member.description = raw.description;
      member.phone = raw.phone;
    } else {
      this.contactList!.members.push({
        id: null,
        dirtyStatus: DirtyStatus.NEW,
        name: raw.name,
        description: raw.description,
        phone: raw.phone,
        ExternalContactListId: this.contactList!.id,
        order: this.members.length + 1,
      });
    }

    this.saveExternalContactList();
  }

  deleteMember() {
    // Remove element from contactListMembers and save the list
    const raw = this.externalContactCreateForm.getRawValue();
    const array = this.contactList?.members.filter((m) => m.id === raw.id);
    if (array && array.length > 0) {
      const member = array[0];
      member.dirtyStatus = DirtyStatus.DELETED;
      this.contactList!.members = [
        ...this.contactList!.members.filter((m) => m.id !== raw.id),
      ];
    }

    this.saveExternalContactList();
  }

  setEditting() {
    this.isEditting = true;
    this.form?.enable();
  }

  saveExternalContactList() {
    if (!this.canEdit && !this.canCreate) {
      return;
    }
    const dv = getDirtyValues(this.form);
    this.saving = true;
    this.isEditting = false;
    const updateStatement =
      this._contactListBuilder.buildExternalContactListUpdateStatement(
        this.contactList!,
        dv
      );
    if (this.contactList?.id) {
      this.updateContactList(updateStatement);
    } else {
      this.createContactList(updateStatement);
    }
  }

  updateContactList(updateStatement: any) {
    if (!this.canEdit) {
      return;
    }
    this._contactListApi
      .updateExternalContactList(this.contactList!.id, updateStatement)
      .subscribe({
        next: (externalList) => {
          this.handleContactListSave(externalList);
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'CONTACT.MESSAGES.ERROR.UPDATE_EXTERNAL_LIST',
            bodyParams: { name: this.contactList?.name },
          });
          this.saving = false;
        },
      });
  }

  createContactList(updateStatement: any) {
    if (!this.canCreate) {
      return;
    }
    this._contactListApi.createExternalContactList(updateStatement).subscribe({
      next: (externalList) => {
        this.handleContactListSave(externalList);
      },
      error: (error) => {
        console.error(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'CONTACT.MESSAGES.ERROR.CREATE_EXTERNAL_LIST',
        });
        this.saving = false;
      },
    });
  }

  async handleContactListSave(data: ExternalContactList) {
    this._logAndMessage.translateToSuccessMessage({
      bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
      headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
    });
    this._contactListTableService.setSelected(data);
    this.contactList = data;
    this.id = data.id;
    this.saving = false;
    this.isEditting = false;
    this.isEdittingMember = false;
    this.displayDialog = false;
    this.updateForm();
    this.form?.disable();
    this.form?.markAsPristine();
    this.updateActionsList();
    this.changesEvent.emit({
      isDirty: false,
      index: this.index(),
      id: this.id,
    });
    const label = await firstValueFrom(
      this._translateService.get('CONTACT.LABEL.EXTERNAL_LIST')
    );
    const tab = this._tabService.getTabById(this.contactList!.id);
    this._tabService.updateTabLabel(
      tab!,
      label + ': ' + this.contactList!.name
    );
  }

  deleteExternalList() {
    if (!this.canDelete) {
      return;
    }

    let confHeader = null,
      ok = null,
      cancel = null,
      message = null;
    const confHeader$ = this._translateService.get(
      'CONTACT.MESSAGES.HEADERS.DELETE_EXTERNAL_CONTACT_LIST'
    );
    const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
    const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
    const message$ = this._translateService.get(
      'CONTACT.MESSAGES.CONFIRMATION.DELETE_EXTERNAL_CONTACT_LIST',
      { name: this.contactList!.name }
    );

    forkJoin([confHeader$, ok$, cancel$, message$]).subscribe((messages) => {
      confHeader = messages[0];
      ok = messages[1];
      cancel = messages[2];
      message = messages[3];
    });

    this._confirmationService.confirm({
      header: confHeader!,
      icon: 'fa fa-question-circle',
      acceptVisible: true,
      rejectVisible: true,
      acceptLabel: ok!,
      rejectLabel: cancel!,
      message: message!,
      accept: () => {
        const id = this.contactList!.id;
        this.saving = true;
        this._contactListApi.deleteExternalContactList(id).subscribe({
          next: (result) => {
            this._logAndMessage.translateToSuccessMessage({
              bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
              headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
            });
            this._contactListTableService.clearResults();
            this.saving = false;
          },
          error: (error) => {
            console.error(error);
            this._logAndMessage.translateToErrorAlert({
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
              bodyKey: 'CONTACT.MESSAGES.ERROR.DELETE_EXTERNAL_LIST',
            });
            this.saving = false;
          },
          complete: () => {
            if (this._deviceService.isMobile()) {
              const tab = this._tabService.buildNewTab(
                EXTERNAL_LIST_SEARCH_CONTAINER,
                true
              );
              this._tabService.setMobileTab(tab);
            } else {
              const index = this._tabService.getActiveIndex();
              this._tabService.closeTab(index);
            }
          },
        });
      },
      reject: () => {},
    });
  }

  resetEdittingMember() {
    this.isEdittingMember = false;
  }

  updateActionsList() {
    const selectAction$ = this._translateService.get(
      'COMMON.LABEL.SELECT_ACTION'
    );
    const viewAudit$ = this._translateService.get(
      'COMMON.LABEL.VIEW_AUDIT_INFO'
    );

    let select,
      audit = null;
    forkJoin([selectAction$, viewAudit$])
      .pipe(take(1))
      .subscribe((messages) => {
        select = messages[0];
        audit = messages[1];

        this.actions = [
          { label: select, value: null },
          { label: audit, value: VIEW_AUDIT },
        ];

        // default actions here
        this.actions = [...this.actions];
      });
  }

  actionEvent($event: SelectChangeEvent, object?: any) {
    if ($event.value) {
      this.selectedAction = null;
      if ($event.value) {
        if ($event.value === VIEW_AUDIT) {
          const history$ = this._contactListApi.getExternalContactListHistories(
            this.contactList!.id
          );
          this._dialogService.open(AuditDialogContainerComponent, {
            dismissableMask: true,
            modal: true,
            closable: true,
            width: '25%',
            data: {
              history: history$,
            },
          });
        }
      }

      if (!isNullOrUndefined(object)) {
        object.clear(null);
      }
    }
  }

  override showActionsDropdown(): boolean {
    return (
      !isNullOrUndefined(this.contactList) &&
      !isNullOrUndefined(this.contactList!.id) &&
      !this._deviceService.isMobile()
    );
  }

  fixMask(field: string) {
    const fieldValue = this.form!.controls[field];
    if (fieldValue) {
      const value: string = fieldValue.value;
      if (value.indexOf('_') > -1) {
        this.form!.controls[field].setValue('');
        this.form?.updateValueAndValidity();
      }
    }
  }
}
